// O contrato das telas dos avisos: Plataforma › Mensagens WhatsApp (modelos,
// envios, regras) e Meus avisos. Tipos puros, sem drizzle — o client importa.

import type {
  AvisoCategoria,
  AvisoChave,
  AvisoEntrega,
  AvisoFilaStatus,
  AvisoPublico,
  RegrasAviso,
  VariavelAviso,
} from "./avisos.js";

export interface CanalAvisoDTO {
  /** true só em produção sem GZAPPY_SANDBOX — a mesma regra de `ehEnvioReal()`. */
  real: boolean;
  rotulo: string;
}

export interface NumerosDaSemanaDTO {
  enviadas: number;
  pessoas: number;
  falhas: number;
  ultimaFalha: string | null;
  agrupadasEventos: number;
  agrupadasMensagens: number;
  seguradas: number;
}

export interface ModeloResumoDTO {
  chave: AvisoChave;
  nome: string;
  publico: AvisoPublico;
  categoria: AvisoCategoria;
  entrega: AvisoEntrega;
  gatilho: string;
  eventoRotulo: string;
  enviadas30d: number;
  falhas30d: number;
  pausado: boolean;
  /** null = o texto padrão do código. */
  versao: { numero: number; em: string; por: string | null } | null;
}

export interface MensagensResumoDTO {
  canal: CanalAvisoDTO;
  semana: NumerosDaSemanaDTO;
  modelos: ModeloResumoDTO[];
}

export interface VersaoDTO {
  versao: number;
  texto: string;
  textoGrupo: string | null;
  em: string;
  por: string | null;
  ativa: boolean;
}

export interface ModeloDetalheDTO {
  chave: AvisoChave;
  nome: string;
  publico: AvisoPublico;
  categoria: AvisoCategoria;
  entrega: AvisoEntrega;
  gatilho: string;
  eventoRotulo: string;
  variaveis: VariavelAviso[];
  /** null quando o modelo não agrupa. */
  variaveisGrupo: VariavelAviso[] | null;
  textoPadrao: string;
  textoGrupoPadrao: string | null;
  amostras: { um: Record<string, string>; grupo: Record<string, string> | null };
  pausado: boolean;
  /** O que está no ar: a versão ativa ou o padrão do código (`versao: null`). */
  ativo: { versao: number | null; texto: string; textoGrupo: string | null };
  versoes: VersaoDTO[];
  proximaVersao: number;
  teste: {
    pode: boolean;
    /** Por que não dá (sem telefone), para o botão desabilitado dizer. */
    motivo: string | null;
    /** O número de quem clicou, mascarado. */
    destino: string | null;
    sandbox: boolean;
  };
  canal: CanalAvisoDTO;
}

export interface PublicarAvisoInput {
  texto: string;
  /** Só nos modelos que agrupam; ausente = mantém o texto de grupo atual. */
  textoGrupo?: string | null;
}

export interface ErrosDeTextoDTO {
  um: Array<{ codigo: string; rotulo: string }>;
  grupo: Array<{ codigo: string; rotulo: string }>;
}

export interface EnviarTesteInput {
  /** Qual amostra: um aviso, o texto de grupo, ou com um campo vazio. */
  amostra: "um" | "grupo" | "vazio";
  /** O texto do editor (ainda não publicado). Ausente = o que está no ar. */
  texto?: string;
  textoGrupo?: string | null;
}

export interface EnviarTesteDTO {
  ok: boolean;
  destino: string;
  sandbox: boolean;
  texto: string;
  erro?: string;
}

// ─── Envios ──────────────────────────────────────────────────────────────────

export const FILTROS_ENVIO = ["todos", "ENVIADA", "SEGURADA", "SUPRIMIDA", "CANCELADA", "FALHOU", "PENDENTE"] as const;
export type FiltroEnvio = typeof FILTROS_ENVIO[number];

export const ROTULO_STATUS_AVISO: Record<AvisoFilaStatus, string> = {
  PENDENTE: "Na fila",
  ENVIADA: "Enviada",
  AGRUPADA: "Agrupada",
  SEGURADA: "Segurada",
  SUPRIMIDA: "Suprimida",
  CANCELADA: "Cancelada",
  FALHOU: "Falhou",
};

export interface EnvioDTO {
  id: string;
  /** ISO — quando entrou na fila. */
  quando: string;
  /** "24/09 07:14", no fuso das regras. */
  quandoTexto: string;
  chave: string;
  modeloNome: string;
  pessoa: { nome: string; org: string; telefone: string };
  status: AvisoFilaStatus;
  /** Motivo, "3 agrupados", "sai 06:30" — o que a coluna Detalhe mostra. */
  detalhe: string;
  podeTentarDeNovo: boolean;
}

export interface EnviosDTO {
  contagens: Record<FiltroEnvio, number>;
  linhas: EnvioDTO[];
}

/** Filtro de texto da tela Envios — pessoa, telefone (dígitos) ou modelo. */
export function casaBuscaEnvio(e: EnvioDTO, q: string): boolean {
  const termo = q.trim().toLowerCase();
  if (!termo) return true;
  const digitos = termo.replace(/\D/g, "");
  return e.pessoa.nome.toLowerCase().indexOf(termo) >= 0
    || e.pessoa.org.toLowerCase().indexOf(termo) >= 0
    || e.chave.indexOf(termo) >= 0
    || e.modeloNome.toLowerCase().indexOf(termo) >= 0
    || (digitos.length >= 2 && e.pessoa.telefone.replace(/\D/g, "").indexOf(digitos) >= 0);
}

// ─── Regras ──────────────────────────────────────────────────────────────────

export interface RegrasAvisoDTO extends RegrasAviso {
  canal: CanalAvisoDTO;
  /** Avisos esperando na fila agora — o que sai ao religar. */
  naFila: number;
}

export type PatchRegrasAviso = Partial<Pick<RegrasAviso, "envioLigado" | "silencioInicio" | "silencioFim" | "janelaMin" | "limiteDia">>;

// ─── Meus avisos ─────────────────────────────────────────────────────────────

export interface CategoriaMeusAvisosDTO {
  categoria: AvisoCategoria;
  rotulo: string;
  descricao: string;
  ativo: boolean;
  /** A frase que aparece ao desligar — o que a pessoa deixa de saber. */
  aoDesligar: string;
}

export interface MeusAvisosDTO {
  /** Mascarado. null = sem telefone na conta. */
  telefone: string | null;
  /** Quem administra a org troca o número no cadastro de usuários; os outros pedem a quem administra. */
  podeTrocarNumero: boolean;
  categorias: CategoriaMeusAvisosDTO[];
}

/** O que cada categoria cobre, por lado — Meus avisos só mostra as do lado da org. */
export const CATEGORIA_POR_LADO: Record<"PRODUTOR" | "COMPRADOR", Partial<Record<AvisoCategoria, { descricao: string; aoDesligar: string }>>> = {
  PRODUTOR: {
    ROMANEIOS: {
      descricao: "Divergência na conferência do comprador, comprador que entrou no Rizoma",
      aoDesligar: "Você não vai saber pelo WhatsApp quando o comprador conferir com diferença.",
    },
    PAGAMENTOS: {
      descricao: "Pagamento registrado pelo comprador, vencimento alterado",
      aoDesligar: "Você não vai saber pelo WhatsApp quando o comprador registrar um pagamento.",
    },
    LEMBRETES: { descricao: "Lembretes de prazo", aoDesligar: "Você não vai receber lembretes de prazo pelo WhatsApp." },
    RESUMOS: {
      descricao: "Resumo da semana (segunda, 7h)",
      aoDesligar: "Você não vai receber o resumo da semana pelo WhatsApp.",
    },
  },
  COMPRADOR: {
    ROMANEIOS: {
      descricao: "Romaneio recebido, corrigido, divergência, registro confirmado pelo produtor",
      aoDesligar: "Você não vai saber pelo WhatsApp quando um produtor formalizar ou corrigir um romaneio.",
    },
    PAGAMENTOS: {
      descricao: "Pagamento contestado pelo produtor, vencimento alterado",
      aoDesligar: "Você não vai saber pelo WhatsApp quando um produtor não reconhecer um pagamento.",
    },
    LEMBRETES: {
      descricao: "Vencimentos do dia (6h30)",
      aoDesligar: "Você não vai receber o lembrete dos vencimentos pelo WhatsApp.",
    },
    RESUMOS: {
      descricao: "Resumo do dia (7h, em dia útil)",
      aoDesligar: "Você não vai receber o resumo do dia pelo WhatsApp.",
    },
  },
};
